import React from "react";
import { JsonLd } from "../components/common/JsonLd";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_ADDITIONAL_OFFERS } from "../utils/constants";
import { TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import {
  BookOpenIcon,
  AcademicCapIcon,
  HandRaisedIcon,
  HeartIcon,
  UserGroupIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import pageData from "../data/pages/zusatzangebote.json";
import { buildSeoPageGraph } from "../utils/structured-data";

type OfferSection = {
  title: string;
  content: string[];
  details?: {
    label: string;
    value: string;
  }[];
  texts?: string[];
  isMainSection?: boolean;
  icon?: "book" | "academic" | "hand" | "heart" | "group" | "bolt";
};

const data = { sections: pageData.sections as OfferSection[] };

const testimonials = [
  {
    quote:
      "Mein Morbus Sudeck hat mein Leben ganz schön auf den Kopf gestellt. Dank der Ergotherapie kann ich meine Hand wieder im Alltag einsetzen. Auch die Gespräche über meine Ängste und Sorgen haben mir sehr gut getan.",
    section: "Handtherapie",
  },
];

const IconComponent: React.FC<{ icon?: OfferSection["icon"] }> = ({ icon }) => {
  switch (icon) {
    case "book":
      return <BookOpenIcon className="h-6 w-6" />;
    case "academic":
      return <AcademicCapIcon className="h-6 w-6" />;
    case "hand":
      return <HandRaisedIcon className="h-6 w-6" />;
    case "heart":
      return <HeartIcon className="h-6 w-6" />;
    case "group":
      return <UserGroupIcon className="h-6 w-6" />;
    case "bolt":
      return <BoltIcon className="h-6 w-6" />;
    default:
      return null;
  }
};

export default function OtherOffersPage() {
  return (
    <>
      <SEOHelmet page="otherOffers" pathname={PATH_ADDITIONAL_OFFERS} />
      <JsonLd data={buildSeoPageGraph(PATH_ADDITIONAL_OFFERS, "otherOffers")} />

      <main id="main-content" tabIndex={-1} className="space-y-8 sm:space-y-14">
        {data.sections.map((section, index) => (
          <article key={index}>
            {section.isMainSection ? (
              section.title === "Weitere Angebote" ? (
                <section aria-labelledby={`section-${index}`} className="relative">
                  <div className="relative">
                    <h1
                      id={`section-${index}`}
                      className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
                    >
                      {section.title}
                    </h1>
                    <div className="space-y-3">
                      {section.texts?.map((text, textIndex) => (
                        <p key={textIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                          {text}
                        </p>
                      ))}
                    </div>
                  </div>
                </section>
              ) : (
                <section aria-labelledby={`section-${index}`} className="relative">
                  <div className="relative">
                    <div className="flex items-start sm:items-center gap-4 sm:gap-6 mb-4">
                      {section.icon && (
                        <div className="p-3 sm:p-4 rounded-xl bg-ergo-100/80 dark:bg-ergo-900/80 shadow-sm transform group-hover:scale-105 transition-all duration-300 flex-shrink-0">
                          <div className="text-ergo-600 dark:text-ergo-400">
                            <IconComponent icon={section.icon} />
                          </div>
                        </div>
                      )}
                      <h2
                        id={`section-${index}`}
                        className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white"
                      >
                        {section.title}
                      </h2>
                    </div>

                    <div className="relative bg-white dark:bg-gray-800/60 rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/60 dark:border-gray-700/40 p-6 sm:p-8 md:p-10 transition-all duration-300">
                      <div className="space-y-6 sm:space-y-8">
                        {section.content.length > 0 && (
                          <div className="space-y-3">
                            {section.content.map((item, itemIndex) => (
                              <p key={itemIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                                {item}
                              </p>
                            ))}
                          </div>
                        )}

                        {section.texts?.map((text, textIndex) => (
                          <p key={textIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                            {text}
                          </p>
                        ))}

                        {section.details && (
                          <dl className="grid gap-4 sm:gap-6">
                            {section.details.map((detail, detailIndex) => (
                              <div key={detailIndex} className="flex flex-col sm:flex-row sm:gap-4">
                                <dt className="text-base sm:text-lg font-medium text-gray-900 dark:text-white sm:w-32 flex-shrink-0">
                                  {detail.label}
                                </dt>
                                <dd className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                                  {detail.value}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </div>

                    {testimonials.map(
                      (testimonial, tIndex) =>
                        testimonial.section === section.title && (
                          <div key={tIndex} className="mt-8">
                            <TestimonialsComponent testimonials={[{ quote: testimonial.quote }]} />
                          </div>
                        )
                    )}
                  </div>
                </section>
              )
            ) : (
              <section aria-labelledby={`section-${index}`} className="relative">
                <div className="relative">
                  <h2
                    id={`section-${index}`}
                    className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                  >
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <p key={itemIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </p>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        ))}
      </main>
    </>
  );
}
